import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import Loading from 'src/components/Loading';
import { APIConfig } from 'src/config';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import usePortfolioAddUserByEmailMutation from 'src/mutations/PortfolioAddUserByEmailMutation';
import FlashesStore from 'src/stores/FlashesStore';
import { PORTFOLIO_USER_ROLE_OWNER } from 'src/util/constants';

import Form from './Form';

const AdminPortfolioUserMemberCreateFragment = graphql`
  fragment AdminPortfolioUserMemberCreate_portfolio on Portfolio {
    uuid
    title
    active {start finish}
    userMembers {
      edges {
        node {
          uuid
          role
        }
      }
    }
  }
`;

/**
 * @param {object} props
 *@returns {React.ReactComponentElement} - AdminPortfolioUserMemberCreate component
 */
function AdminPortfolioUserMemberCreate(props) {
  if (!props) {
    return <Loading />;
  }

  let { portfolio } = props;
  portfolio = useFragment(AdminPortfolioUserMemberCreateFragment, portfolio);
  const {
    active, title: portfolioTitle, uuid: portfolioUuid, userMembers,
  } = portfolio || {};

  const { start: portfolioStart } = active || {};

  const intl = useIntl();
  const pageTitle = intl.formatMessage({ id: 'admin.admin_portfolio.admin_portfolio_user_member_create.page.title', defaultMessage: 'Enosi Admin - Portfolio user member invite' });
  const breadcrumbAdmin = intl.formatMessage({ id: 'admin.admin_portfolio.admin_portfolio_user_member_create.breadcrumbs.admin.label', defaultMessage: 'Admin' });
  const breadcrumbPortfolios = intl.formatMessage({ id: 'admin.admin_portfolio.admin_portfolio_user_member_create.breadcrumbs.portfolio.label', defaultMessage: 'Portfolios' });
  const breadcrumbInvitePortfolioUserMember = intl.formatMessage({ id: 'admin.admin_portfolio.admin_portfolio_user_member_create.breadcrumbs.portfolio_user_member_create.label', defaultMessage: 'Invite portfolio user member' });

  const userMembersRoles = userMembers.edges.map((edge) => edge.node.role);
  const portfolioHasOwner = userMembersRoles.includes(PORTFOLIO_USER_ROLE_OWNER);

  const [processing, setProcessing] = useState(false);
  const { router } = props;
  const mutationName = 'portfolioAddUserByEmail';

  const handlePortfolioUserMemberInviteSuccess = (response, errors) => {
    setProcessing(false);

    if (errors && errors.length > 0) {
      errors.forEach((error) => FlashesStore.flash(FlashesStore.ERROR, {
        message: FlashesStore.ERROR,
        source: {
          errors: [error],
        },
      }, mutationName));
      return;
    }

    const { uuid } = response.portfolioAddUserByEmail;

    FlashesStore.flash(
      FlashesStore.SUCCESS,
      <span>
        {intl.formatMessage({ id: 'admin.admin_portfolio.admin_portfolio_user_member_create.response_message.success', defaultMessage: 'Successfully added portfolio user member {uuid}.' }, { uuid })}
      </span>,
      mutationName,
    );

    router.push(`/admin/portfolio-user-members/${uuid}`);
  };

  const handlePortfolioUserMemberInviteFailure = (error) => {
    setProcessing(false);

    FlashesStore.flash(FlashesStore.ERROR, error, mutationName);
  };
  const portfolioAddUserByEmail = usePortfolioAddUserByEmailMutation();

  const handlePortfolioUserMemberInvite = (input) => {
    setProcessing(true);
    portfolioAddUserByEmail(
      portfolioUuid,
      input,
      handlePortfolioUserMemberInviteSuccess,
      handlePortfolioUserMemberInviteFailure,
    );
  };
  const portfolioStartTime = portfolioStart ? DateTime.fromSeconds(portfolioStart) : null;
  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          { name: breadcrumbAdmin, path: '/admin' },
          { name: breadcrumbPortfolios, path: '/admin/portfolios' },
          { name: portfolioTitle, path: `/admin/portfolios/${portfolioUuid}` },
          { name: breadcrumbInvitePortfolioUserMember },
        ]}
      />

      <Form
        handleInvite={handlePortfolioUserMemberInvite}
        processing={processing}
        portfolioHasOwner={portfolioHasOwner}
        minStart={portfolioStartTime}
        timezone={APIConfig().DEFAULT_TIMEZONE}
      />
    </>
  );
}

export default AdminPortfolioUserMemberCreate;

AdminPortfolioUserMemberCreate.propTypes = {
  router: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  portfolio: PropTypes.object.isRequired,
};
